import { DeckInsertionPosition, DeckState, Position } from '../types';
import { SNAP_GRID_SIZE } from '../constants';

const toRadians = (rotation: number): number => (rotation * Math.PI) / 180;

export const getRotationVector = (
  rotation: number,
  axis: 'right' | 'down'
): Position => {
  const radians = toRadians(rotation);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  if (axis === 'right') {
    return { x: cos, y: sin };
  }

  return { x: -sin, y: cos };
};

export const mapScreenPosition = (
  clientX: number,
  clientY: number,
  containerRect: DOMRect,
  invertView: boolean = false
): Position => {
  const localX = clientX - containerRect.left;
  const localY = clientY - containerRect.top;
  
  if (invertView) {
    return {
      x: containerRect.width - localX,
      y: containerRect.height - localY
    };
  }
  
  return {
    x: localX,
    y: localY
  };
};

/**
 * Get mouse position adjusted for scale and position
 */
export const getScaledMousePosition = (
  e: React.MouseEvent,
  containerRect: DOMRect,
  position: Position,
  scale: number,
  invertView: boolean = false
): Position => {
  const localPos = mapScreenPosition(e.clientX, e.clientY, containerRect, invertView);
  return {
    x: (localPos.x - position.x) / scale,
    y: (localPos.y - position.y) / scale,
  };
};

export const getScaledClientPosition = (
  clientX: number,
  clientY: number,
  containerRect: DOMRect,
  position: Position,
  scale: number,
  invertView: boolean = false
): Position => {
  const localPos = mapScreenPosition(clientX, clientY, containerRect, invertView);
  return {
    x: (localPos.x - position.x) / scale,
    y: (localPos.y - position.y) / scale
  };
};

/**
 * Snap a position to the grid
 */
export const snapToGrid = (position: Position): Position => {
  return {
    x: Math.round(position.x / SNAP_GRID_SIZE) * SNAP_GRID_SIZE,
    y: Math.round(position.y / SNAP_GRID_SIZE) * SNAP_GRID_SIZE,
  };
};

/**
 * Calculate center position for a new item based on current viewport
 */
export const calculateCenterPosition = (
  width: number,
  height: number,
  position: Position,
  scale: number
): Position => {
  return {
    x: (width / 2 - position.x) / scale,
    y: (height / 2 - position.y) / scale,
  };
};

/**
 * Check if a card is hovering over a deck (collision detection)
 */
export const isCardOverDeck = (
  cardPosition: Position,
  deckPosition: Position,
  cardWidth: number,
  cardHeight: number
): boolean => {
  // For simplicity with rotated elements, we use a center point and radius check
  const deckCenterX = deckPosition.x + cardWidth / 2;
  const deckCenterY = deckPosition.y + cardHeight / 2;
  
  const cardCenterX = cardPosition.x + cardWidth / 2;
  const cardCenterY = cardPosition.y + cardHeight / 2;
  
  const dx = cardCenterX - deckCenterX;
  const dy = cardCenterY - deckCenterY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  // Use a hit radius that's approximately half the card diagonal
  const hitRadius = Math.sqrt(cardWidth * cardWidth + cardHeight * cardHeight) / 2.5;
  
  return distance <= hitRadius;
};

export const calculateDeckHoverTarget = (
  cardPosition: Position,
  decks: Record<string, DeckState>,
  cardWidth: number,
  cardHeight: number
): { deckId: string | null; zone: DeckInsertionPosition | null } => {
  let hoveredDeckId: string | null = null;

  for (const [deckId, deck] of Object.entries(decks)) {
    if (isCardOverDeck(cardPosition, deck.position, cardWidth, cardHeight)) {
      hoveredDeckId = deckId;
    }
  }

  if (!hoveredDeckId) {
    return { deckId: null, zone: null };
  }

  const hoveredDeck = decks[hoveredDeckId];
  if (!hoveredDeck) {
    return { deckId: null, zone: null };
  }

  const deckCenterX = hoveredDeck.position.x + cardWidth / 2;
  const deckCenterY = hoveredDeck.position.y + cardHeight / 2;
  const cardCenterX = cardPosition.x + cardWidth / 2;
  const cardCenterY = cardPosition.y + cardHeight / 2;
  const downVector = getRotationVector(hoveredDeck.rotation, 'down');
  const relativeX = cardCenterX - deckCenterX;
  const relativeY = cardCenterY - deckCenterY;
  const downProjection = relativeX * downVector.x + relativeY * downVector.y;

  const zone: DeckInsertionPosition = downProjection < 0 ? 'top' : 'bottom';

  return { deckId: hoveredDeckId, zone };
};

/**
 * Calculate new zoom position to zoom toward mouse cursor
 */
export const calculateZoomPosition = (
  mouseX: number,
  mouseY: number,
  currentPosition: Position,
  zoomFactor: number
): Position => {
  return {
    x: mouseX - (mouseX - currentPosition.x) * zoomFactor,
    y: mouseY - (mouseY - currentPosition.y) * zoomFactor,
  };
}; 
